import React, { useCallback, useState } from 'react';
import IdentifyBox, { Identity } from './IdentifyBox';

// A step only some people may do alone, asked on the spot (Fernando 19.10: "Jam.. call a manager … I don't trust her
// when there is a re-print option or none who is a beginner"; 20.10: Tier 1 "can not use the Pack manually button").
// The server decides (pp_identity.approval_gate): 428 means someone with the right has to approve — this box asks for
// their PIN or badge and sends the same request again with it. A person who has the right never sees the box.
// Every reprint button uses useReprintApproval; the old packing screen's Pack button useManualPackApproval. -- Claude

type Send = (approver: Record<string, string>) => Promise<Response>;
type Ask = { message: string; submit: (id: Identity) => Promise<string | null>; cancel: () => void };

const detailOf = async (r: Response, fallback: string) => {
  const d = await r.json().catch(() => ({} as any));
  return typeof d.detail === 'string' ? d.detail : fallback;
};

export function useApproval(sub: string, cancelLabel = 'Cancel') {
  const [ask, setAsk] = useState<Ask | null>(null);

  // send(approver) makes the request; approver is {} first, then {approver_pin} or {approver_badge}.
  // Resolves with the final Response, or null when the approval was cancelled.
  const run = useCallback((send: Send) => new Promise<Response | null>((resolve, reject) => {
    send({}).then(async first => {
      if (first.status !== 428) { resolve(first); return; }
      const message = await detailOf(first, 'This needs a manager');
      setAsk({
        message,
        submit: async id => {
          const r = await send(id.badge ? { approver_badge: id.badge } : { approver_pin: id.pin || '' });
          // the request itself passed the first time, so a refusal now is about the approver: say it, stay open
          if ([400, 401, 403, 404, 428].includes(r.status)) return detailOf(r, 'Not approved');
          setAsk(null);
          resolve(r);
          return null;
        },
        cancel: () => { setAsk(null); resolve(null); },
      });
    }).catch(reject);
  }), []);

  const box = ask && (
    <IdentifyBox title={ask.message} cancelLabel={cancelLabel} onSubmit={ask.submit} onCancel={ask.cancel} sub={sub} />
  );
  return { run, box };
}

export function useReprintApproval(cancelLabel = 'Cancel') {
  return useApproval('Printer jam or a torn label? A manager enters their PIN or scans their badge here.', cancelLabel);
}

export function useManualPackApproval(cancelLabel = 'Cancel') {
  return useApproval('Nobody here with the right? Cancel and use Issue → Can\'t find.', cancelLabel);
}
